import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, BookOpen, Lightbulb, GraduationCap, Compass, Target, CheckCircle, AlertTriangle } from 'lucide-react';

// Import des composants modulaires
import ObjectifCard from './ObjectifCard';

// Clé pour le stockage dans localStorage
const ACTIVE_PHASE_STORAGE_KEY = 'aladinn_active_phase';

const AladinnLanding = ({ userId, onSelectCompetence, currentCompetence }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // État pour la phase active avec initialisation depuis localStorage
  const [activePhase, setActivePhase] = useState(() => {
    try {
      const savedPhase = localStorage.getItem(ACTIVE_PHASE_STORAGE_KEY);
      return savedPhase ? parseInt(savedPhase, 10) : 1;
    } catch (e) {
      // En cas d'erreur avec localStorage (e.g. mode privé)
      console.warn("Erreur lors de l'accès à localStorage:", e);
      return 1;
    }
  });
  
  // État pour gérer les alertes
  const [showAlert, setShowAlert] = useState({
    visible: false,
    type: 'info',
    message: ''
  });

  // Fermer l'alerte après un certain délai
  useEffect(() => {
    if (showAlert.visible) {
      const timer = setTimeout(() => {
        setShowAlert(prev => ({ ...prev, visible: false }));
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [showAlert.visible]);
  
  // Fonction sécurisée pour modifier localStorage
  const savePhaseToStorage = (phase) => {
    try {
      localStorage.setItem(ACTIVE_PHASE_STORAGE_KEY, phase.toString());
    } catch (e) {
      console.warn("Erreur lors de l'écriture dans localStorage:", e);
    }
  };
  
  // Détecter la phase active basée sur l'URL actuelle
  useEffect(() => {
    const path = location.pathname;
    let newPhase = null;
    
    // Déterminer la phase en fonction de l'URL actuelle
    if (path.includes('/HomePage') && !path.includes('/Recommandations/') && !path.includes('/actionPlan/')) {
      newPhase = 1; // Ambition - parcours général ou étape ambition
    } else if (path.includes('/Recommandations/') || path.includes('/intent-analysis/') || 
               path.includes('/webSource/') || path.includes('/webSearch/')) {
      newPhase = 2; // Stratégie
    } else if (path.includes('/actionPlan/') || 
              (path.includes('/competencesList') && location.state?.fromAction)) {
      newPhase = 3; // Action
    }
    
    // Mettre à jour seulement si une nouvelle phase a été déterminée
    if (newPhase !== null) {
      setActivePhase(newPhase);
      savePhaseToStorage(newPhase);
    }
  }, [location.pathname, location.state]);
  
  // Persister la phase active dans localStorage quand elle change
  useEffect(() => {
    savePhaseToStorage(activePhase);
    console.log("Phase active mise à jour:", activePhase);
  }, [activePhase]);
  
  // Fonction de gestion des clics sur les phases
  const handlePhaseClick = (phaseNumber) => {
    // Mettre à jour la phase active avant la navigation
    setActivePhase(phaseNumber);
    savePhaseToStorage(phaseNumber);
    
    if (phaseNumber === 1) {
      // Phase Ambition - MODIFICATION : Navigation vers le parcours au lieu de formulation-objectif
      navigate('/HomePage', { state: { initialStep: 'ambition' } });
    } else if (phaseNumber === 2) {
      // Phase Stratégie
      navigate('/HomePage', { state: { initialStep: 'strategie' } });
      }
      else if (phaseNumber === 3) {
      navigate('/HomePage', { state: { initialStep: 'action' } });
    }
  };
  
  // Définition des phases avec l'état actif
  const phases = [
    {
      step: 1,
      title: "Ambition",
      description: "Exprimez clairement votre ambition pour commencer votre parcours de transformation.",
      icon: "target",
      isActive: activePhase === 1,
      isClickable: true,
      badge: activePhase === 1 ? "Phase active" : "Commencer ici"
    },
    {
      step: 2,
      title: "Stratégie",
      description: "Explorez les différentes approches et établissez un plan concret pour atteindre votre objectif.",
      icon: "book",
      isActive: activePhase === 2,
      isClickable: true,
      badge: activePhase === 2 ? "Phase active" : null
    },
    {
      step: 3,
      title: "Plan d'actions",
      description: "Passez à l'action avec des outils concrets et suivez votre progression vers le succès.",
      icon: "graduation",
      isActive: activePhase === 3,
      isClickable: true,
      badge: activePhase === 3 ? "Phase active" : null
    }
  ];
  
  // Définition des espaces disponibles avec leur description mise à jour
  const espaces = [
    {
      title: "Espace Stratégie",
      icon: <Compass className="h-12 w-12 text-blue-600 mx-auto" />,
      gradient: "from-blue-100 to-blue-50",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      borderHover: "hover:border-blue-300",
      features: [
        "Recevez des recommandations personnalisées",
        "Explorez différentes approches et méthodologies",
        "Créez un plan d'action concret et réalisable"
      ]
    },
    {
      title: "Espace Action",
      icon: <Target className="h-12 w-12 text-purple-600 mx-auto" />,
      gradient: "from-purple-100 to-purple-50",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
      borderHover: "hover:border-purple-300",
      features: [
        "Suivez votre progression vers vos ambitions",
        "Visualisez vos objectifs selon leur phase",
        "Identifiez les actions prioritaires"
      ]
    },
    {
      title: "Espace Exploration",
      icon: <BookOpen className="h-12 w-12 text-green-600 mx-auto" />,
      gradient: "from-green-100 to-green-50",
      color: "text-green-600",
      bgColor: "bg-green-100",
      borderHover: "hover:border-green-300",
      features: [
        "Consultez la base de connaissances thématique",
        "Testez vos acquis avec des quiz interactifs",
        "Approfondissez vos compétences avec des exercices ciblés"
      ]
    },
    {
      title: "Espace Inspiration",
      icon: <Lightbulb className="h-12 w-12 text-yellow-600 mx-auto" />,
      gradient: "from-yellow-100 to-yellow-50",
      color: "text-yellow-600",
      bgColor: "bg-yellow-100",
      borderHover: "hover:border-yellow-300",
      features: [
        "Découvrez des solutions innovantes et inspirantes",
        "Explorez les objectifs similaires aux vôtres",
        "Trouvez de nouvelles pistes et opportunités"
      ]
    }
  ];
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-purple-50 to-white">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Message d'alerte si nécessaire */}
        {showAlert.visible && (
          <div className={`mb-6 p-4 rounded-lg border ${
            showAlert.type === 'warning' ? 'bg-yellow-50 border-yellow-300 text-yellow-800' :
            showAlert.type === 'error' ? 'bg-red-50 border-red-300 text-red-800' :
            'bg-blue-50 border-blue-300 text-blue-800'
          }`}>
            <div className="flex items-center">
              {showAlert.type === 'warning' ? (
                <AlertTriangle className="h-5 w-5 mr-2" />
              ) : showAlert.type === 'error' ? (
                <AlertTriangle className="h-5 w-5 mr-2" />
              ) : (
                <CheckCircle className="h-5 w-5 mr-2" />
              )}
              <p>{showAlert.message}</p>
              <button 
                onClick={() => setShowAlert(prev => ({ ...prev, visible: false }))}
                className="ml-auto text-gray-600 hover:text-gray-800"
              >
                ×
              </button>
            </div>
          </div>
        )}
        
        {/* En-tête avec Sparkles icon - mis à jour */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-purple-100 rounded-full mb-6">
            <Sparkles className="h-8 w-8 text-purple-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
            Transformez vos ambitions en réalité
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Formulez votre objectif et découvrez comment Aladinn peut vous aider à l'atteindre.
          </p>
        </div>
        
        {/* Phases du parcours - mise à jour avec les nouveaux termes */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">Votre parcours de transformation</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {phases.map((phase, index) => (
              <ObjectifCard 
                key={index}
                step={phase.step}
                title={phase.title}
                description={phase.description}
                icon={phase.icon}
                isActive={phase.isActive}
                isClickable={phase.isClickable}
                onClick={() => handlePhaseClick(phase.step)}
                badge={phase.badge}
              />
            ))}
          </div>
        </div>

        {/* Section compétence active si une compétence est sélectionnée */}
        {currentCompetence && (
          <div className="mb-16 bg-white p-6 rounded-xl shadow-md border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Votre ambition actuelle</h2>
            <div className="flex items-center p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="bg-blue-100 p-2 rounded-full mr-4">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-blue-800">{currentCompetence.title}</h3>
                {currentCompetence.shortTitle && (
                  <p className="text-sm text-blue-600 mt-1">{currentCompetence.shortTitle}</p>
                )}
              </div>
              <div className="ml-auto">
                <button 
                  onClick={() => {
                    setActivePhase(2); // Définir la phase de stratégie comme active
                    savePhaseToStorage(2);
                    navigate(`/Recommandations/${currentCompetence.id}`, { 
                      state: { competence: currentCompetence, userId }
                    });
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Poursuivre
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Espaces disponibles */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Des espaces dédiés pour chaque étape de votre parcours
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {espaces.map((espace, index) => (
              <div 
                key={index}
                className={`bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 ${espace.borderHover} hover:shadow-lg`}
              >
                <div className={`bg-gradient-to-r ${espace.gradient} p-6 text-center`}>
                  {espace.icon}
                  <h3 className="text-xl font-semibold text-gray-800 mt-3">
                    {espace.title}
                  </h3>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {espace.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <div className={`flex-shrink-0 h-5 w-5 rounded-full ${espace.bgColor} flex items-center justify-center mt-0.5`}>
                          <span className={`${espace.color} text-xs`}>✓</span>
                        </div>
                        <span className="ml-3 text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default AladinnLanding;